<script setup lang="ts">
import { ChevronDownIcon } from '@blpde/nx-icons';
import { computed, inject, useId } from 'vue';
import NavBarSubMenu from './NavBarSubMenu.vue';
import { navBarThemeKey } from './internal';
import { opensInNewTab } from './link';
import { opensPanel, panelIdOf } from './panel';
import type { NavBarNode } from './types';

const props = defineProps<{
  node: NavBarNode;
  /** Whether this button is the focused root entry. */
  highlighted: boolean;
  /** Whether the popover under this button is open. */
  open: boolean;
  /** True if button visuals should hide the chevron (overflow trigger). */
  suppressChevron?: boolean;
  focusedId: string | null;
  openChildIds: readonly string[];
  /** Set by the hidden measurement row only: no panel trigger there. */
  ghost?: boolean;
  /**
   * Set by NavBar on a visible, keyed left root entry while the bar is
   * `reorderable`: the sortable's attributes for the entry's wrapper and the
   * pointer half for its trigger.
   */
  reorder?: { itemProps: Record<string, unknown>; pointerProps: Record<string, unknown> };
}>();

const emit = defineEmits<{
  activate: [event: PointerEvent | KeyboardEvent];
  activateChild: [child: NavBarNode, event: PointerEvent | KeyboardEvent];
  hoverChild: [child: NavBarNode];
  pointerEnter: [];
}>();

const theme = inject(navBarThemeKey, 'dark');
const badgeId = `navbar-badge-${useId()}`;
const hasChildren = computed(() => props.node.children.length > 0);
/**
 * The entry opens its own panel. NavBar renders the panel itself, outside the
 * menubar; the panel finds this entry by its `aria-controls`. Never in the
 * measurement row.
 */
const hasPanel = computed(() => !props.ghost && opensPanel(props.node));
/**
 * A leaf with a target is a real link, so the browser opens it in a new tab.
 * An entry with a panel ignores `href`.
 */
const linkHref = computed(() =>
  !hasChildren.value && !props.node.panel && !props.node.disabled ? props.node.href : undefined
);

function onClick(event: MouseEvent): void {
  // Middle / Ctrl- / Cmd-click on a link: the browser's new tab, not ours.
  if (linkHref.value && opensInNewTab(event)) return;
  if (linkHref.value) event.preventDefault();
  emit('activate', event as PointerEvent);
}

const badge = computed(() => {
  const value = props.node.badge;
  if (value === null || value === undefined || value === '') return '';
  return String(value);
});
</script>

<template>
  <div class="navbar-root-anchor" v-bind="reorder?.itemProps">
    <component
      :is="linkHref ? 'a' : 'button'"
      :type="linkHref ? undefined : 'button'"
      :href="linkHref"
      role="menuitem"
      :class="['menubar-trigger', theme]"
      :data-state="open ? 'open' : undefined"
      :data-highlighted="highlighted ? '' : undefined"
      :data-id="node.id"
      :tabindex="highlighted ? 0 : -1"
      :aria-haspopup="hasChildren ? 'menu' : hasPanel ? 'dialog' : undefined"
      :aria-expanded="hasChildren || hasPanel ? open : undefined"
      :aria-controls="hasPanel ? panelIdOf(node) : undefined"
      :aria-label="node.iconOnly ? node.text : undefined"
      :aria-describedby="badge ? badgeId : undefined"
      :disabled="linkHref ? undefined : node.disabled || undefined"
      :aria-disabled="node.disabled || undefined"
      :aria-keyshortcuts="reorder ? 'Alt+ArrowLeft Alt+ArrowRight' : undefined"
      v-bind="reorder?.pointerProps"
      @click="onClick"
      @pointerenter="emit('pointerEnter')"
    >
      <component :is="node.icon" v-if="node.icon" filled class="nxt-icon-lg" />
      <span v-if="!node.iconOnly">{{ node.text }}</span>
      <ChevronDownIcon v-if="hasChildren && !suppressChevron" />
      <!-- Out of the name, read as the description instead: an icon-only entry's
           `aria-label` would otherwise drop the count, a text entry's name would
           run it into its text. -->
      <span v-if="badge" :id="badgeId" class="menubar-trigger-badge" aria-hidden="true">
        {{ badge }}
      </span>
    </component>
    <NavBarSubMenu
      v-if="hasChildren"
      :parent="node"
      :is-root="true"
      :open="open"
      :focused-id="focusedId"
      :open-child-ids="openChildIds"
      @activate-child="(c, e) => emit('activateChild', c, e)"
      @hover-child="(c) => emit('hoverChild', c)"
    />
  </div>
</template>

<style scoped>
@import url('./style.css');

.navbar-root-anchor {
  position: relative;
  display: inline-flex;
}

/* Reordering (NavBar `reorderable`). The dragged entry follows the pointer
   (the sortable moves the wrapper); the landing slot is a bar in the gap. */
.navbar-root-anchor[data-nx-sortable-state='pointer'] > .menubar-trigger {
  cursor: grabbing;
  box-shadow: var(--nxt-shadow-md);

  &.light {
    background-color: var(--nxt-brand-subtle-bg);
  }
  &.dark {
    background-color: var(--nxt-navbar-item-hover-bg);
  }
}

.navbar-root-anchor[data-nx-drop-side] > .menubar-trigger::after {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  inline-size: calc(var(--nxt-hairline) * 3);
  border-radius: var(--nxt-radius-full);
  pointer-events: none;
}

.navbar-root-anchor[data-nx-drop-side] > .menubar-trigger.light::after {
  background: var(--nxt-brand-bg);
}

.navbar-root-anchor[data-nx-drop-side] > .menubar-trigger.dark::after {
  background: var(--nxt-navbar-fg);
}

.navbar-root-anchor[data-nx-drop-side='before'] > .menubar-trigger::after {
  left: calc(var(--nxt-space-2xs) / -2 - var(--nxt-hairline) * 1.5);
}

.navbar-root-anchor[data-nx-drop-side='after'] > .menubar-trigger::after {
  right: calc(var(--nxt-space-2xs) / -2 - var(--nxt-hairline) * 1.5);
}

.menubar-trigger:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  pointer-events: none;
}

.menubar-trigger-badge {
  position: absolute;
  top: calc(var(--nxt-space-2xs) / 2);
  right: calc(var(--nxt-space-2xs) / 2);
  min-width: calc(var(--nxt-size-unit) * 0.938);
  height: calc(var(--nxt-size-unit) * 0.938);
  padding: 0 var(--nxt-space-2xs);
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-danger-bg);
  border: var(--nxt-hairline) solid currentColor;
  color: var(--nxt-danger-fg);
  font-size: calc(var(--nxt-font-size-xs) * 0.85);
  font-weight: var(--nxt-font-weight-bold);
  line-height: 1;
}
</style>
